import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { register as apiRegister } from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { formatDueDayOptions } from '../utils/formatters';
import { InfoAlert } from '../components/Alert';

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  // Same return-to contract as Login. A buyer who needs to CREATE an account
  // mid-purchase is the more common case on a new tenant's site, so this page
  // had the same defect and needs the same fix -- registering logs you in, and
  // it must return you to the checkout you left.
  const from = location.state?.from?.pathname || '/dashboard';
  const notice = location.state?.message || '';
  const { login } = useAuth();
  const { getCompanyLegalName, getCompanyEmail, getCompanyPhone, getCurePeriodDays, getGracePeriodDays, getPaymentDueDayOptions } = useConfig();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Dynamically load reCAPTCHA script if site key is configured
  useEffect(() => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaSiteKey && !window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
      script.async = true;
      script.onload = () => {
        // Wait for grecaptcha to be ready
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      };
      document.head.appendChild(script);
    } else if (window.grecaptcha) {
      setRecaptchaLoaded(true);
    }
  }, []);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate terms accepted
    if (!termsAccepted) {
      setError('You must read and accept the Terms of Service to register');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      let recaptchaToken = null;

      // Only use reCAPTCHA if site key is configured
      const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (recaptchaSiteKey) {
        // Ensure grecaptcha is loaded
        if (!window.grecaptcha || !recaptchaLoaded) {
          setError('reCAPTCHA is still loading. Please wait a moment and try again.');
          setLoading(false);
          return;
        }

        // Get reCAPTCHA token
        recaptchaToken = await window.grecaptcha.execute(
          recaptchaSiteKey,
          { action: 'register' }
        );

        console.log('reCAPTCHA token generated:', recaptchaToken);
      }

      // Send registration (with or without reCAPTCHA token)
      const response = await apiRegister({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        ...(recaptchaToken && { recaptchaToken })
      });
      
      login(response.data.user, response.data.token);
      navigate(from);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Create Your Account</h2>

        {/* Not dismissible: it is the reason this page is on screen. */}
        {notice && <InfoAlert message={notice} dismissible={false} />}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name:</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name:</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Phone (optional):</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Password:</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
            />
          </div>

          <div className="form-group">
            <label>Confirm Password:</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          {error && <div className="error-message" style={{ marginBottom: '15px' }}>{error}</div>}
          
          {/* Terms of Service */}
          <div style={{ marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="btn"
              style={{
                width: '100%',
                backgroundColor: termsAccepted ? '#28a745' : 'var(--forest-green)',
                color: 'white'
              }}
            >
              {termsAccepted ? '✓ Terms of Service Accepted' : 'Read & Accept Terms of Service'}
            </button>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-full-width"
            disabled={loading || !termsAccepted}
          >
            {loading ? 'Creating Account...' : 'Register'}
          </button>
        </form>

        <div className="auth-links">
          {/* Carries the return path back the other way, so a buyer who lands
              on Register first and already has an account is not stranded. */}
          Already have an account? <Link to="/login" state={location.state}>Login here</Link>
        </div>
        
        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
          <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '20px', textAlign: 'center' }}>
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
          </div>
        )}
      </div>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-brand-text)' }}>Terms of Service</h2>
            
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text)' }}>
              <h3>Agreement to Terms</h3>
              <p>By registering, you agree to be bound by these Terms of Service. These Terms govern your use of our website and services.</p>
              
              <h3>Eligibility</h3>
              <p>You must be at least 18 years of age and have the legal capacity to enter into binding contracts.</p>
              
              <h3>Contract for Deed</h3>
              <p>All land purchases are made through a Contract for Deed. Legal title remains with {getCompanyLegalName()} until the contract is paid in full.</p>
              
              <h3>Payment Terms</h3>
              <ul>
                <li>Monthly payments must be made on your selected due date ({formatDueDayOptions(getPaymentDueDayOptions())})</li>
                <li>Late payments ({getGracePeriodDays()}+ days past due) incur late fees</li>
                <li>You may prepay without penalty at any time</li>
              </ul>
              
              <h3>Default and Forfeiture</h3>
              <p><strong>Important:</strong> If you default on payments and do not cure within {getCurePeriodDays()} days of written notice, all payments made will be forfeited and you will lose all rights to the property.</p>
              
              <h3>Property "As-Is"</h3>
              <p>Properties are sold "AS IS" without warranty. You are responsible for conducting your own due diligence.</p>
              
              <h3>Governing Law</h3>
              <p>These Terms shall be governed by the laws of the State of Wisconsin. Disputes shall be resolved exclusively in the courts of Outagamie County, Appleton, Wisconsin.</p>
              
              <p style={{ marginTop: '20px', padding: '10px', background: 'var(--color-surface-sunken)', borderRadius: '5px' }}>
                <strong>Contact:</strong> {getCompanyEmail()} | {getCompanyPhone()}
              </p>
              
              <p style={{ textAlign: 'center', marginTop: '15px', fontSize: '12px', color: 'var(--color-text-light)' }}>
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand-text)' }}>View Full Terms of Service (Opens in New Window)</a>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                className="btn"
                style={{
                  flex: 1,
                  backgroundColor: 'var(--forest-green)',
                  color: 'white',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                ✓ I Accept the Terms of Service
              </button>
              <button
                onClick={() => setShowTermsModal(false)}
                className="btn"
                style={{
                  flex: 1,
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;